import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { adminApi, apiError } from "../api";

const KINDS = [
  { key: "itineraries", label: "Trip Requests" },
  { key: "contacts", label: "Contact Messages" },
  { key: "bookings", label: "Bookings" },
];

const STATUSES = ["New", "Contacted", "Closed"];
const STATUS_COLORS = { New: "bg-rose-500/10 text-rose-600", Contacted: "bg-amber-500/10 text-amber-600", Closed: "bg-emerald-500/10 text-emerald-600" };

export default function Enquiries() {
  const [kind, setKind] = useState("itineraries");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminApi.get(`/admin/enquiries/${kind}`).then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/enquiries/${kind}/${id}`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await adminApi.delete(`/admin/enquiries/${kind}/${id}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid="admin-enquiries-page">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Customer Enquiries</h1>
          <p className="text-sm text-muted-foreground">All submissions from the website forms.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="enquiries-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
      </div>

      <Tabs value={kind} onValueChange={setKind} className="mb-5">
        <TabsList>
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`enquiries-tab-${k.key}`}>{k.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground rounded-2xl border border-dashed">No enquiries here yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((x) => (
            <div key={x.id} className="rounded-xl border bg-white dark:bg-slate-900 p-4" data-testid={`enquiry-${x.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold">{x.name || x.full_name}</div>
                  <div className="text-xs text-muted-foreground">{x.email} {x.phone && `· ${x.phone}`} · {fmt(x.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[x.status] || STATUS_COLORS.New}`}>{STATUSES.includes(x.status) ? x.status : "New"}</span>
                  <Select value={STATUSES.includes(x.status) ? x.status : "New"} onValueChange={(v) => setStatus(x.id, v)}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid={`status-select-${x.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(x.id)} data-testid={`delete-enquiry-${x.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                {kind === "itineraries" && (
                  <>
                    <div><b className="text-foreground">Tour:</b> {x.destination}</div>
                    <div><b className="text-foreground">Travelers:</b> {x.travelers} · <b className="text-foreground">Budget:</b> {x.budget}</div>
                    <div><b className="text-foreground">Dates:</b> {x.travel_dates}</div>
                    {x.special_requests && <div className="sm:col-span-2"><b className="text-foreground">Message:</b> {x.special_requests}</div>}
                  </>
                )}
                {kind === "contacts" && (
                  <>
                    {x.subject && <div><b className="text-foreground">Subject:</b> {x.subject}</div>}
                    <div className="sm:col-span-2"><b className="text-foreground">Message:</b> {x.message}</div>
                  </>
                )}
                {kind === "bookings" && (
                  <>
                    <div><b className="text-foreground">Tour:</b> {x.package_title}</div>
                    <div><b className="text-foreground">Amount:</b> ₹{(x.package_price || 0).toLocaleString("en-IN")} · <b className="text-foreground">Travelers:</b> {x.travelers}</div>
                    <div><b className="text-foreground">Start:</b> {x.start_date}</div>
                    {x.notes && <div className="sm:col-span-2"><b className="text-foreground">Notes:</b> {x.notes}</div>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
